import React, { useState } from 'react';
import Dialog from '@components/Atoms/Dialog';
import Select from '@components/Atoms/Select';

const BELLRINGER_PREVIEW_OPTION = 'preview';
export const BELLRINGER_STAY_OPTION = 'stay';

const DEFAULT_TITLE = 'Who is in the classroom today?';
const DEFAULT_FIRST_OPTION: BellringerFirstOption = {
  label: "I'm just previewing / preparing",
  value: BELLRINGER_PREVIEW_OPTION,
  intent: 'preview',
};

export interface BellringerSection {
  id: number;
  name: string;
}

/**
 * The non-section option shown at the top of the dropdown. The initial launch
 * pop (CONTE-1523) uses the "previewing" variant; the inactivity nudge uses a
 * "still working on it" variant. Selecting it yields `sectionId: null` and the
 * option's own `intent`.
 */
export interface BellringerFirstOption {
  label: string;
  value: string;
  intent: 'preview' | 'stay';
}

export interface BellringerContextDialogProps {
  open: boolean;
  sections: BellringerSection[];
  onCancel: () => void;
  onContinue: (payload: {
    intent: 'teaching' | 'preview' | 'stay';
    sectionId: number | null;
  }) => void;
  /** Dialog prompt. Defaults to the initial-launch prompt. */
  title?: string;
  /** Non-section first option. Defaults to the "previewing" option. */
  firstOption?: BellringerFirstOption;
  /** Whether to render the Cancel/abort button. Defaults to true. */
  showCancel?: boolean;
}

const BellringerContextDialog = ({
  open,
  sections,
  onCancel,
  onContinue,
  title = DEFAULT_TITLE,
  firstOption = DEFAULT_FIRST_OPTION,
  showCancel = true,
}: BellringerContextDialogProps): React.ReactElement => {
  const [selectedValue, setSelectedValue] = useState<string | number>('');

  const options = [
    { label: firstOption.label, value: firstOption.value },
    ...sections.map((section) => ({ label: section.name, value: section.id })),
  ];

  const handleCancel = () => {
    setSelectedValue('');
    onCancel();
  };

  const handleContinue = () => {
    if (selectedValue === '') {
      return;
    }
    if (selectedValue === firstOption.value) {
      onContinue({ intent: firstOption.intent, sectionId: null });
    } else {
      onContinue({ intent: 'teaching', sectionId: Number(selectedValue) });
    }
    setSelectedValue('');
  };

  const cancelAction = {
    label: 'Cancel',
    onClick: handleCancel,
    buttonProps: {
      variant: 'outlined' as const,
      dataTestId: 'bellringer-context-cancel',
    },
  };
  const continueAction = {
    label: 'Continue',
    onClick: handleContinue,
    buttonProps: {
      variant: 'contained' as const,
      dataTestId: 'bellringer-context-continue',
      disabled: selectedValue === '',
    },
  };

  return (
    <Dialog
      open={open}
      title={title}
      aria-label={title}
      showCloseIcon={false}
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') {
          handleCancel();
        }
      }}
      data-testid="bellringer-context-dialog"
      actions={showCancel ? [cancelAction, continueAction] : [continueAction]}
    >
      <Select
        id="bellringer-context-select"
        label="Class"
        size="medium"
        fullWidth
        list={options}
        value={selectedValue}
        onChangeHandler={(value) => setSelectedValue(value as string | number)}
      />
    </Dialog>
  );
};

export default BellringerContextDialog;
